import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext";
import { useApiCollection } from "../hooks/useApiCollection";
import { MEDIA } from "../lib/site";
import { Gavel, LineChart, Map, ArrowUpRight, Check } from "lucide-react";

const ICONS = {
  gavel: <Gavel className="h-5 w-5" />,
  "line-chart": <LineChart className="h-5 w-5" />,
  map: <Map className="h-5 w-5" />,
};

const MEDIA_BY_SLUG = {
  expertize: MEDIA.accident,
  evaluare: MEDIA.blueprint,
  cartografie: MEDIA.topo,
};

export default function Services() {
  const { t, lang } = useLanguage();
  const { data: services } = useApiCollection("/services", []);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#0d0d0d] border-y border-neutral-900 py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-12 gap-8 mb-16">
          <div className="col-span-12 md:col-span-4">
            <div className="overline mb-4">{t.services.overline}</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-white">
              {t.services.title}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6 flex md:justify-end">
            <p className="text-neutral-400 text-base sm:text-lg max-w-md leading-relaxed">
              {t.services.subtitle}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((item, idx) => {
            const title = lang === "ro" ? item.title_ro : item.title_en;
            const desc = lang === "ro" ? item.desc_ro : item.desc_en;
            const bullets = lang === "ro" ? item.bullets_ro : item.bullets_en;
            const tag = String(item.order).padStart(2, "0");
            return (
              <motion.article
                key={item.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                data-testid={`service-card-${item.slug}`}
                className="group relative bg-[#0A0A0A] border border-neutral-800 hover:border-yellow-500/60 transition-colors duration-300 flex flex-col"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                    style={{
                      backgroundImage: `url(${MEDIA_BY_SLUG[item.slug] || MEDIA.hero})`,
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute top-4 left-4 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-yellow-400">
                    / {tag}
                  </div>
                  <div className="absolute top-4 right-4 h-9 w-9 bg-yellow-500/10 backdrop-blur-sm border border-yellow-500/30 flex items-center justify-center text-yellow-400">
                    {ICONS[item.icon] || <Gavel className="h-5 w-5" />}
                  </div>
                </div>

                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display font-semibold text-white text-xl leading-snug">
                    {title}
                  </h3>
                  <p className="mt-3 text-sm text-neutral-400 leading-relaxed flex-1">
                    {desc}
                  </p>
                  <ul className="mt-5 space-y-2">
                    {(bullets || []).map((b) => (
                      <li
                        key={b}
                        className="flex items-center gap-2 text-xs font-mono-tech text-neutral-300"
                      >
                        <Check className="h-3 w-3 text-yellow-400" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#contact"
                    data-testid={`service-cta-${item.slug}`}
                    className="mt-6 inline-flex items-center justify-between gap-2 border border-neutral-800 hover:border-yellow-500 hover:bg-yellow-500 hover:text-black transition-colors px-4 py-3 font-mono-tech text-xs uppercase tracking-[0.15em] text-neutral-200"
                  >
                    <span>{lang === "ro" ? "Solicită" : "Request"}</span>
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
